import { useState } from 'react'
import BookingModal from '../components/BookingModal'
import FontAwesomeIcon from '../components/FontAwesomeIcon'
import { Link } from 'react-router-dom'

export default function LakeBunyonyi() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedExperience, setSelectedExperience] = useState('')

  const handleBooking = (experience) => {
    setSelectedExperience(experience)
    setIsModalOpen(true)
  }

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-80 md:h-96 bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(/images/lake-bunyonyi-hero.jpg)',
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
            Lake Bunyonyi
          </h1>
          <p className="text-base md:text-lg max-w-2xl mx-auto">
            The Lake of a Thousand Islands - Uganda's Scenic Paradise
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-bold text-safari-green mb-4">Destination Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Nestled in the terraced hills of southwestern Uganda near Kabale, Lake Bunyonyi is one of Africa's most
                scenic and peaceful destinations. Meaning "place of many little birds" in the local language, this stunning
                lake is dotted with 29 islands and surrounded by steep hillsides cultivated with crops. At depths reaching
                up to 900 meters, it's one of the deepest lakes in Africa.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Lake Bunyonyi offers a tranquil retreat perfect for relaxation after gorilla trekking in nearby Bwindi.
                The lake is bilharzia-free and safe for swimming, making it one of the few African lakes where you can
                safely take a dip. With its cool highland climate, stunning scenery, and array of water activities, Lake
                Bunyonyi provides an idyllic escape into Uganda's natural beauty.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Islands</h3>
                  <p className="text-gray-700">29 islands</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Depth</h3>
                  <p className="text-gray-700">Up to 900m</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Bird Species</h3>
                  <p className="text-gray-700">200+</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-bold text-safari-green mb-2">Swimming</h3>
                  <p className="text-gray-700">Bilharzia-free & safe</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="/images/lake-bunyonyi-hero.jpg"
                alt="Lake Bunyonyi"
                className="w-full h-96 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How to Get There */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">How to Get There</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-car" className="w-6 h-6" />
                  From Kampala
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Distance:</strong> Approximately 410 km via Mbarara</p>
                  <p><strong>Travel Time:</strong> 6-7 hours by road through scenic southwestern Uganda</p>
                  <p><strong>Route:</strong> Kampala → Masaka → Mbarara → Kabale → Lake Bunyonyi</p>
                  <p className="text-sm italic">Well-maintained tarmac road. 4WD not essential but comfortable for hillside lodges.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-mountain" className="w-6 h-6" />
                  From Bwindi
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>From Buhoma Sector:</strong> 3-4 hours through scenic mountain roads</p>
                  <p><strong>From Nkuringo/Rushaga:</strong> 2-3 hours, closer proximity</p>
                  <p><strong>Perfect Combination:</strong> Many travelers visit Lake Bunyonyi after gorilla trekking for relaxation</p>
                  <p className="text-sm italic">Highly recommended to combine gorilla trekking with lakeside relaxation.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-globe" className="w-6 h-6" />
                  From Kigali, Rwanda
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Distance:</strong> Approximately 150 km via Gatuna/Katuna border</p>
                  <p><strong>Travel Time:</strong> 3-4 hours from Kigali</p>
                  <p><strong>Border Crossing:</strong> Straightforward with East Africa Tourist Visa</p>
                  <p className="text-sm italic">Shorter route than from Kampala. Popular for international visitors.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Highlights */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Key Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-island-tropical" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">29 Scenic Islands</h3>
                <p className="text-gray-700">
                  Each island has its own unique character and story. Explore islands with resorts, community settlements, and historical significance including the famous Punishment Island.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-swimming" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Safe Swimming</h3>
                <p className="text-gray-700">
                  One of the few African lakes completely free of bilharzia and crocodiles. Enjoy safe swimming in crystal-clear mountain waters surrounded by breathtaking scenery.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-dove" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Birdwatcher's Paradise</h3>
                <p className="text-gray-700">
                  Over 200 bird species inhabit the lake and surrounding hills. Spot grey crowned cranes, herons, weaverbirds, and many more in this ornithological haven.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-mountain-sun" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Terraced Hillsides</h3>
                <p className="text-gray-700">
                  The lake is surrounded by dramatic terraced hills cultivated by local communities. The agricultural landscape creates stunning photographic opportunities at every turn.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-users" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Cultural Experiences</h3>
                <p className="text-gray-700">
                  Visit local Bakiga communities, learn traditional farming methods, experience local music and dance, and hear fascinating stories about island history and legends.
                </p>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-32 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-temperature-low" className="w-16 h-16 text-white" />
              </div>
              <div className="p-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Cool Highland Climate</h3>
                <p className="text-gray-700">
                  At 1,962 meters above sea level, enjoy refreshingly cool temperatures. Perfect escape from Uganda's tropical heat with pleasant days and cool evenings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Activities & Experiences */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Activities & Experiences</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-rowing" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Canoe & Kayak Paddling</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Explore 29 stunning islands by dugout canoe or kayak. The lake is bilharzia-free and safe for swimming, offering a peaceful paddle through pristine waters with spectacular scenery.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-4 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $15 per person</p>
                <Link to="/activities/lake-bunyonyi-canoeing" className="btn-primary w-full text-center block">
                  View Experience
                </Link>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-person-snowboarding" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Zip-Lining Over the Lake</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Soar above Lake Bunyonyi's crystal waters on an exhilarating zip-line. Multiple lines offer different speeds and heights for an unforgettable aerial adventure.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 1-2 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $60 per person</p>
                <button onClick={() => handleBooking('Zip-Lining - Lake Bunyonyi')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-hiking" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Island Trail Hiking</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Hike to Akampene (Punishment Island) and learn its fascinating history. Explore other islands with local guides sharing stories of tradition and culture.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy to Moderate</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $20 per person</p>
                <button onClick={() => handleBooking('Island Trail Hiking - Lake Bunyonyi')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
            <div className="card group">
              <div className="relative h-40 overflow-hidden flex-shrink-0 bg-gradient-to-br from-safari-green to-safari-gold flex items-center justify-center">
                <FontAwesomeIcon icon="fa-binoculars" className="w-20 h-20 text-white" />
              </div>
              <div className="p-6 flex flex-col flex-grow">
                <h3 className="text-lg font-bold text-safari-green mb-2">Birdwatching Boat Trips</h3>
                <p className="text-gray-700 mb-4 flex-grow">
                  Spot over 200 bird species including weavers, herons, and kingfishers. Watch for otters and observe local fishermen catching crayfish in traditional ways.
                </p>
                <div className="space-y-2 mb-4 text-sm text-gray-600">
                  <p><FontAwesomeIcon icon="fa-clock" className="w-4 h-4 mr-2" />Duration: 2-3 hours</p>
                  <p><FontAwesomeIcon icon="fa-chart-simple" className="w-4 h-4 mr-2" />Difficulty: Easy</p>
                </div>
                <p className="text-safari-gold font-bold mb-3 text-center text-lg">From $30 per person</p>
                <button onClick={() => handleBooking('Birdwatching Boat Trips - Lake Bunyonyi')} className="btn-primary w-full">
                  Book Experience
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Travel Tips</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6" />
                  Best Time to Visit
                </h3>
                <div className="space-y-3 text-gray-700">
                  <p><strong>Year-Round Destination:</strong> Lake Bunyonyi can be visited any time with its pleasant highland climate.</p>
                  <p><strong>Dry Seasons (June-August, December-February):</strong> Best for outdoor activities, clearer views, and ideal canoeing conditions.</p>
                  <p><strong>Wet Seasons (March-May, October-November):</strong> Lush green landscapes, fewer tourists, still enjoyable for most activities.</p>
                  <p className="text-sm italic">Often combined with Bwindi gorilla trekking. Plan 2-3 days for full relaxation.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                  What to Pack
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Essential:</strong> Swimsuit, light layers, warm jacket for cool evenings, comfortable walking shoes</p>
                  <p><strong>Recommended:</strong> Sunscreen, sunglasses, hat, binoculars for birdwatching, camera for scenery</p>
                  <p><strong>Weather Gear:</strong> Light rain jacket, as showers can occur year-round in highlands</p>
                  <p className="text-sm italic">Evenings can be cool at 1,962m elevation. Pack a sweater or light jacket.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Swimming Safety:</strong> Lake is bilharzia-free and safe for swimming. No crocodiles or hippos.</p>
                  <p><strong>Altitude:</strong> At 1,962m, most people adjust quickly. Drink plenty of water.</p>
                  <p><strong>Malaria:</strong> Lower risk at this altitude but prophylaxis still recommended.</p>
                  <p><strong>Sun Protection:</strong> Highland sun can be strong. Use sunscreen even on cloudy days.</p>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-coins" className="w-6 h-6" />
                  Budgeting Tips
                </h3>
                <div className="space-y-2 text-gray-700">
                  <p><strong>Accommodation:</strong> Wide range from budget backpackers to luxury island resorts. Book island lodges in advance.</p>
                  <p><strong>Activities:</strong> Many lodges include canoes and basic activities. Guided tours extra.</p>
                  <p><strong>Food:</strong> Most accommodations offer full board. Some have à la carte options.</p>
                  <p><strong>Duration:</strong> Budget 2-3 nights for full experience. Perfect post-gorilla trekking relaxation.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where to Stay */}
      <section className="py-16 bg-safari-light">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-6 text-center">Where to Stay</h2>
          <p className="text-center text-gray-700 mb-12 max-w-3xl mx-auto">
            Accommodation options range from mainland lodges to island retreats, each offering stunning lake views and unique experiences.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg">
              <thead className="bg-safari-green text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Category</th>
                  <th className="px-6 py-4 text-left">Accommodation Name</th>
                  <th className="px-6 py-4 text-left">Location</th>
                  <th className="px-6 py-4 text-left">Features</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Birdnest Resort</td>
                  <td className="px-6 py-4">Private island</td>
                  <td className="px-6 py-4">Luxury cottages, infinity pool, spa, gourmet dining, exclusive</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Luxury</td>
                  <td className="px-6 py-4">Arcadia Cottages Lake Bunyonyi</td>
                  <td className="px-6 py-4">Itambira Island</td>
                  <td className="px-6 py-4">Eco-luxury cottages, panoramic views, organic gardens, peaceful</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Bunyonyi Overland Resort</td>
                  <td className="px-6 py-4">Mainland, hillside location</td>
                  <td className="px-6 py-4">Comfortable cottages, restaurant, bar, lake access, great views</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Mid-Range</td>
                  <td className="px-6 py-4">Crater Bay Cottages</td>
                  <td className="px-6 py-4">Lakeside, mainland</td>
                  <td className="px-6 py-4">Bandas with lake views, restaurant, swimming platform, canoes</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Byoona Amagara Island Retreat</td>
                  <td className="px-6 py-4">Island location</td>
                  <td className="px-6 py-4">Simple bandas, community-run, eco-friendly, cultural experiences</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-bold text-safari-green">Budget</td>
                  <td className="px-6 py-4">Bunyonyi Rock Resort</td>
                  <td className="px-6 py-4">Mainland</td>
                  <td className="px-6 py-4">Budget rooms, camping, backpacker friendly, social atmosphere</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-2xl font-bold text-safari-green mb-8 text-center">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is it really safe to swim in Lake Bunyonyi?</h3>
                <p className="text-gray-700">
                  Yes, completely safe. Lake Bunyonyi is bilharzia-free and has no crocodiles or hippos. It's one of the few African lakes where swimming is entirely safe. The water is cool and refreshing.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How many days should I spend at Lake Bunyonyi?</h3>
                <p className="text-gray-700">
                  Minimum 2 nights recommended for full relaxation. Many visitors combine 3-4 days at Bunyonyi with Bwindi gorilla trekking. This allows time for island hopping, hiking, and unwinding after adventure activities.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Which is the best island to stay on?</h3>
                <p className="text-gray-700">
                  Depends on preference. Mainland offers easier access and more accommodation options. Island stays (like Birdnest Resort) offer exclusive, peaceful experiences. Both provide stunning views and access to activities.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I visit Punishment Island?</h3>
                <p className="text-gray-700">
                  Yes, Punishment Island is included in most island-hopping tours. Guides share the island's historical significance as a place where unmarried pregnant girls were abandoned. It's a sobering cultural-historical site.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Can I combine Lake Bunyonyi with gorilla trekking?</h3>
                <p className="text-gray-700">
                  Absolutely! This is the most popular combination. Lake Bunyonyi is 2-4 hours from Bwindi sectors, making it perfect for post-trekking relaxation. Many itineraries include both destinations.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">What activities are free vs. paid?</h3>
                <p className="text-gray-700">
                  Most lodges include canoe use free of charge. Swimming and walking around your island are free. Paid activities include guided island tours, cultural visits, birding guides, and boat transfers.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">Is Lake Bunyonyi suitable for families?</h3>
                <p className="text-gray-700">
                  Very suitable! Safe swimming, gentle canoeing, and easy hikes make it perfect for families. Kids can safely enjoy water activities. Many lodges offer family cottages and child-friendly environments.
                </p>
              </div>
              <div className="border-l-4 border-safari-gold pl-6">
                <h3 className="text-lg font-bold text-safari-green mb-2">How cold does it get at night?</h3>
                <p className="text-gray-700">
                  At 1,962m elevation, nights can be cool (10-15°C). Days are pleasant (18-25°C). Pack warm layers for evenings. Most lodges provide extra blankets. The highland climate is refreshing after Uganda's heat.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-safari-green text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Discover Uganda's Most Scenic Lake
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Experience the tranquility of Lake Bunyonyi's 29 islands, enjoy safe swimming in crystal-clear waters, and immerse
            yourself in stunning highland scenery. Perfect for relaxation after your Uganda adventures.
          </p>
          <Link
            to="/plan-your-trip"
            className="btn-primary text-lg inline-block"
          >
            Plan Your Lake Bunyonyi Retreat
          </Link>
        </div>
      </section>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience={selectedExperience}
      />
    </div>
  )
}
